import React, { useEffect } from 'react';
import { FaInfoCircle, FaUser, FaEnvelope, FaBriefcase, FaCalendarAlt, FaBuilding, FaSitemap, FaCommentDots, FaCheckCircle, FaUserTimes } from "react-icons/fa";
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getRequestTerminalAccount } from "redux/request/terminalAccountSlice";
import { formatDate } from "helpers/utils";
import { MdHistoryToggleOff, MdPendingActions } from "react-icons/md";
import RequesTerminalAccountDetailHeader from '../../../components/app/account-management/request-terminal-account/RequesTerminalAccountDetailHeader';
import { Badge, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';

const RequestTerminalAccountDetailPage = () => {
  const { recordData: record, loading } = useSelector((state) => state.request_terminal_account);
  const params = useParams();
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchRecord = async () => {
      try {
        await dispatch(getRequestTerminalAccount(params.id)).unwrap();
      } catch (error) {
        toast.error(`Error fetching user account details: ${error.message}`);
      }
    };
    fetchRecord();
  }, [dispatch, params.id]);

  if (loading) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12">
        <LoadingSpinner size="md" message="Loading request details..." />
      </div>
    );
  }

  const isClosed = record?.closed || false;

  const InfoItem = ({ icon: Icon, label, value, valueColor = "tw-text-slate-900", iconColor = "tw-text-blue-600" }) => (
    <div className="tw-flex tw-items-start tw-mb-3">
      <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-mr-3 tw-bg-blue-50">
        <Icon className={iconColor} style={{ fontSize: '1.1rem' }} />
      </div>
      <div className="tw-flex-1">
        <div className="tw-text-slate-500 tw-mb-1" style={{ fontSize: '0.85rem', fontWeight: '500' }}>
          {label}
        </div>
        <div className={`tw-font-semibold ${valueColor}`} style={{ fontSize: '0.95rem' }}>
          {value || '-'}
        </div>
      </div>
    </div>
  );

  InfoItem.propTypes = {
    icon: PropTypes.elementType.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
    valueColor: PropTypes.string,
    iconColor: PropTypes.string,
  };

  return (
    <>
      <RequesTerminalAccountDetailHeader record={record} />

      {/* Status Banner */}
      <div
        className={`tw-rounded-2xl tw-p-4 tw-mb-4 ${
          isClosed
            ? 'tw-bg-green-50 tw-border tw-border-green-200'
            : 'tw-bg-yellow-50 tw-border tw-border-yellow-200'
        }`}
        style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
      >
        <div className="tw-flex tw-items-start">
          <div
            className={`tw-w-12 tw-h-12 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-mr-3 ${
              isClosed ? 'tw-bg-green-500' : 'tw-bg-yellow-500'
            }`}
          >
            {isClosed ? (
              <FaCheckCircle className="tw-text-white" style={{ fontSize: '1.5rem' }} />
            ) : (
              <MdPendingActions className="tw-text-slate-900" style={{ fontSize: '1.5rem' }} />
            )}
          </div>
          <div className="tw-flex-1">
            <div className="tw-flex tw-items-center tw-mb-2">
              <h5
                className="tw-mb-0 tw-font-bold tw-text-base"
                style={{ color: isClosed ? '#155724' : '#856404' }}
              >
                {isClosed ? 'Request Completed' : 'Request Pending'}
              </h5>
              <span className="tw-ml-3">
                <Badge variant={isClosed ? "success" : "warning"} size="sm">
                  {record.status || 'Pending'}
                </Badge>
              </span>
            </div>
            <p className="tw-mb-0 tw-text-slate-600" style={{ fontSize: '0.9rem' }}>
              {isClosed
                ? 'Your terminal account request has been processed and completed by the IT department.'
                : 'Your request has been submitted to the IT department and is awaiting processing.'}
            </p>
          </div>
        </div>
      </div>

      {/* Submission Info */}
      <div
        className="tw-bg-white tw-rounded-2xl tw-border-0 tw-mb-4"
        style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
      >
        <div className="tw-p-4">
          <div className="tw-flex tw-items-center tw-mb-3">
            <div
              className="tw-flex tw-items-center tw-justify-center tw-mr-3 tw-rounded tw-flex-shrink-0"
              style={{
                width: '36px',
                height: '36px',
                backgroundColor: '#4facfe',
              }}
            >
              <FaInfoCircle className="tw-text-white" style={{ fontSize: '1rem' }} />
            </div>
            <h5 className="tw-mb-0 tw-font-bold" style={{ color: '#0288d1' }}>
              Request Information
            </h5>
          </div>
          <div className="tw-flex tw-items-center tw-text-slate-500" style={{ fontSize: '0.85rem' }}>
            <FaUser className="tw-mr-2" />
            <span>
              Submitted by:{' '}
              <strong className="tw-text-blue-600">{record.created_by || '-'}</strong>
            </span>
            <span className="tw-mx-2">•</span>
            <MdHistoryToggleOff className="tw-mr-2" />
            <span>{record.created_date || '-'}</span>
          </div>
        </div>
      </div>

      {/* User Details */}
      <div
        className="tw-bg-white tw-rounded-2xl tw-border-0 tw-mb-4"
        style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
      >
        <div
          className="tw-px-5 tw-py-4 tw-rounded-t-2xl"
          style={{
            background: 'linear-gradient(135deg, #7b90efff 0%, #363cf4ff 100%)',
          }}
        >
          <h6 className="tw-mb-0 tw-text-white tw-font-bold" style={{ fontSize: '1rem' }}>
            <FaUserTimes className="tw-mr-2 tw-inline" />
            Employee Details
          </h6>
        </div>
        <div className="tw-p-4">
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-0">
            <div>
              <InfoItem
                icon={FaUser}
                label="Full Name"
                value={record.name}
                valueColor="tw-text-slate-900"
              />
              <InfoItem
                icon={FaEnvelope}
                label="Email Address"
                value={record.email}
                valueColor="tw-text-blue-600"
              />
              <InfoItem
                icon={FaBriefcase}
                label="Job Title"
                value={record.title}
                valueColor="tw-text-slate-900"
              />
              <InfoItem
                icon={FaCalendarAlt}
                label="Terminal Date"
                value={formatDate(record.terminal_date)}
                valueColor="tw-text-red-600"
                iconColor="tw-text-red-600"
              />
            </div>
            <div>
              <InfoItem
                icon={FaBuilding}
                label="Site Office"
                value={record?.site?.name}
                valueColor="tw-text-slate-900"
              />
              <InfoItem
                icon={FaSitemap}
                label="Department"
                value={record?.department?.name}
                valueColor="tw-text-slate-900"
              />
              <InfoItem
                icon={FaCommentDots}
                label="Remark"
                value={record.remark}
                valueColor="tw-text-slate-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* IT Response Section */}
      {(record.comment_by_it || record.closed_by || record.closed_date) && (
        <div
          className="tw-bg-white tw-rounded-2xl tw-border-0"
          style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
        >
          <div
            className="tw-px-5 tw-py-4 tw-rounded-t-2xl"
            style={{
              background: isClosed
                ? 'linear-gradient(135deg, #28a745 0%, #218838 100%)'
                : 'linear-gradient(135deg, #ffc107 0%, #e0a800 100%)',
            }}
          >
            <h6
              className="tw-mb-0 tw-font-bold"
              style={{ color: isClosed ? '#fff' : '#000', fontSize: '1rem' }}
            >
              <FaCheckCircle className="tw-mr-2 tw-inline" />
              IT Department Response
            </h6>
          </div>
          <div className="tw-p-4">
            {record.comment_by_it && (
              <div className="tw-mb-4">
                <div
                  className="tw-p-3 tw-rounded"
                  style={{
                    backgroundColor: '#fff3cd',
                    borderLeft: '4px solid #ffc107',
                  }}
                >
                  <div className="tw-text-slate-500 tw-mb-2" style={{ fontSize: '0.85rem', fontWeight: '500' }}>
                    <FaCommentDots className="tw-mr-2 tw-inline" />
                    Comment by IT
                  </div>
                  <div className="tw-text-slate-900" style={{ fontSize: '0.95rem' }}>
                    {record.comment_by_it}
                  </div>
                </div>
              </div>
            )}

            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-0">
              <div>
                {record.closed_by && (
                  <InfoItem
                    icon={FaUser}
                    label="Closed By"
                    value={record.closed_by}
                    valueColor="tw-text-green-600"
                    iconColor="tw-text-green-600"
                  />
                )}
              </div>
              <div>
                {record.closed_date && (
                  <InfoItem
                    icon={FaCalendarAlt}
                    label="Closed Date"
                    value={formatDate(record.closed_date)}
                    valueColor="tw-text-green-600"
                    iconColor="tw-text-green-600"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RequestTerminalAccountDetailPage;
